import { useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { PersonId } from '@shared/brand';
import { seedColorForSubArea } from '@domain/entities/Organization';
import type { IncapacityKind } from '@domain/entities/Incapacity';
import type { DateRange } from '@domain/value-objects/DateRange';
import {
  IMSS_FOLIO_MAX_LENGTH,
  isStorableImssFolio,
  looksLikeCitt,
} from '@domain/value-objects/ImssFolio';
import { getServices } from '@infrastructure/services';
import { Avatar, Chip, cx } from '../../design-system';
import { formatDateRange } from '../../format';
import { useAccess } from '../../hooks/useAccess';
import { useSubAreaName } from '../../hooks/useSubAreaName';
import { useAreaKinds } from '../../hooks/useAreaKinds';
import { useSession } from '../../providers/SessionProvider';
import { useToast } from '../../providers/ToastProvider';
import { WizardShell, wizardStyles as w } from '../../wizard/WizardShell';
import { RangePicker } from '../../wizard/RangePicker';
import { describeUpload } from '../../wizard/uploadMessages';
import styles from './AbsenceWizards.module.css';

/**
 * Registrar incapacidad — the three-step flow, transcribed from
 * `design/reference/screens/registrar-incapacidad.html`.
 *
 * Two things separate it from every other absence in the product. It is counted
 * in natural days, not business days — the IMSS subsidises calendar days, so
 * skipping weekends would understate the leave. And it is a record rather than
 * a request: the rest is already true by the time it is filed, so RR.HH.
 * validates the certificate instead of approving the absence.
 *
 * The middle step's name and its folio requirement both follow the `imss`
 * feature. With it off the company is not filing against the institute, so
 * demanding an IMSS folio would block a legitimate private certificate.
 */

const STEPS_BASE = ['Tipo y fechas', 'Cobertura'] as const;

export const IncapacityWizard = ({
  onClose,
  onSubmitted,
}: {
  onClose: () => void;
  onSubmitted: () => Promise<void>;
}): ReactNode => {
  const session = useSession();
  const { featureOn } = useAccess();
  const subAreaName = useSubAreaName('Tu sub área');
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  const imssOn = featureOn('imss');

  /* Los de su área, no los de la empresa: cada área decide cuáles ofrece y puede
     tener los suyos. Ver `useAreaKinds`. */
  const { incapacityKinds } = useAreaKinds(session.areaId);
  const kinds = useMemo(
    () => incapacityKinds.filter((k) => k.enabled),
    [incapacityKinds],
  );

  const [step, setStep] = useState(0);
  const [kindId, setKindId] = useState(() => kinds[0]?.id ?? '');
  const [range, setRange] = useState<DateRange | null>(null);
  const [folio, setFolio] = useState('');
  const [umf, setUmf] = useState('');
  /** The file's name, for the label. */
  const [certificate, setCertificate] = useState<string | null>(null);
  /** The storage path the upload produced. Null until a file actually lands. */
  const [certificatePath, setCertificatePath] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [replacement, setReplacement] = useState<PersonId | null>(null);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const { holds } = useAccess();
  /* Lo que no puede guardarse, y lo que sólo se advierte. Ver `ImssFolio.ts`. */
  const folioUnusable = !isStorableImssFolio(folio);
  const folioOddShape = folio.trim().length > 0 && !looksLikeCitt(folio);

  const today = getServices().clock.today();
  /** Quien decide sobre ausencias ajenas: RR.HH. valida certificados, el líder aprueba. */
  const canBackdate = holds('admin') || holds('aprobar');

  /**
   * The folder the certificate is uploaded into, generated before the record
   * exists — the wizard demands the document before it can file anything, so
   * there is no incapacity id to key it on yet. Same arrangement as the vacation
   * registro: the person's own prefix is the part that is checked.
   */
  const [draftDocumentId] = useState(() => getServices().ids.generate());


  const holidays = useQuery({
    queryKey: ['wizard', 'holidays', today.year],
    queryFn: async () => {
      const result = await getServices().holidays.nonWorkingDates([today.year, today.year + 1]);
      return result.ok ? result.value : new Set<string>();
    },
  });

  /** Colleagues who could cover, bounded to the person's own sub area. */
  const candidates = useQuery({
    queryKey: ['incWizard', 'candidates', session.areaId, session.subAreaId],
    queryFn: async () => {
      const result = await getServices().people.list(
        session.subAreaId === null
          ? { areaId: session.areaId, status: 'active' }
          : { areaId: session.areaId, subAreaId: session.subAreaId, status: 'active' },
        { limit: 50 },
      );
      if (!result.ok) return [];
      return result.value.items.filter((p) => p.id !== session.personId);
    },
  });

  const kind: IncapacityKind | undefined = kinds.find((k) => k.id === kindId) ?? kinds[0];
  /*
   * Si lo que se registra lo certifica el IMSS.
   *
   * La paternidad vive en este catálogo por decisión de producto —queda a
   * discreción del líder— pero la paga el patrón: no tiene folio del instituto, ni
   * unidad médica, ni certificado sellado. Pedirle los tres sería pedirle papeles
   * que no existen, así que el paso cambia de nombre y deja de exigirlos.
   */
  const imssCertified = kind?.imssCertified !== false;

  const STEPS = useMemo(
    () => [
      { label: STEPS_BASE[0] },
      { label: !imssCertified ? 'Respaldo' : imssOn ? 'Certificado IMSS' : 'Certificado médico' },
      { label: STEPS_BASE[1] },
    ],
    [imssOn, imssCertified],
  );
  const naturalDays = range?.naturalDays ?? 0;

  const summary = useMemo(() => {
    const chosen = (candidates.data ?? []).find((p) => p.id === replacement);
    return [
      { k: 'Tipo', v: kind?.id ?? '—' },
      {
        k: 'Periodo',
        v:
          range === null
            ? '—'
            : `${formatDateRange(range)} · ${String(naturalDays)} ${
                naturalDays === 1 ? 'día natural' : 'días naturales'
              }`,
      },
      {
        k: !imssCertified ? 'Documento' : imssOn ? 'Folio IMSS' : 'Folio del certificado',
        v: folio.trim().length > 0 ? folio.trim() : 'Pendiente',
      },
      { k: 'Unidad médica', v: umf.trim().length > 0 ? `UMF ${umf.trim()}` : '—' },
      { k: 'Certificado', v: certificate ?? 'Pendiente de adjuntar' },
      { k: 'Cobertura', v: chosen?.displayName ?? 'Sin designar' },
    ];
  }, [
    kind,
    range,
    naturalDays,
    imssOn,
    imssCertified,
    folio,
    umf,
    certificate,
    replacement,
    candidates.data,
  ]);

  const advance = (): void => {
    if (step === 0) {
      if (range === null || naturalDays === 0) {
        toast.warn('Revisa las fechas: el fin del reposo debe ser posterior al inicio.');
        return;
      }
      setStep(1);
      return;
    }

    if (step === 1) {
      // Only demanded when the company files against the institute; a private
      // certificate has no IMSS folio to capture.
      if (imssOn && imssCertified && folio.trim().length === 0) {
        toast.warn('Falta el folio: captura el folio del certificado del IMSS.');
        return;
      }
      if (folioUnusable) {
        /* Lo imposible sí bloquea; la forma del CITT no — el servidor rechazaría
           esto y dejarlo avanzar sería prometer un envío que va a fallar. */
        toast.warn('El folio sólo lleva letras, números y guiones.');
        return;
      }
      /* El adjunto sólo donde hay algo que adjuntar. Un padre no tiene certificado
         sellado, y el acta de nacimiento puede tardar días en existir. */
      if (imssCertified && certificatePath === null) {
        toast.warn('Falta el certificado: adjunta el PDF sellado.');
        return;
      }
      setStep(2);
      return;
    }

    if (replacement === null) {
      toast.warn('Falta la cobertura: elige quién cubre tus pendientes.');
      return;
    }

    void submit();
  };

  /** Uploads the sealed certificate and keeps the path it produced. */
  const attachCertificate = async (file: File | undefined): Promise<void> => {
    if (file === undefined) {
      setCertificate(null);
      setCertificatePath(null);
      return;
    }

    setUploading(true);
    const result = await getServices().documents.uploadIncapacityCertificate({
      file,
      fileName: file.name,
      ownerPersonId: session.personId,
      areaId: session.areaId,
      recordId: draftDocumentId,
    });
    setUploading(false);

    if (!result.ok) {
      setCertificate(null);
      setCertificatePath(null);
      toast.bad(describeUpload(result.error));
      return;
    }

    setCertificate(file.name);
    setCertificatePath(result.value);
    toast.ok(`Certificado adjunto: ${file.name}.`);
  };

  const submit = async (): Promise<void> => {
    if (kind === undefined || range === null || replacement === null) return;
    const chosen = (candidates.data ?? []).find((p) => p.id === replacement);
    setBusy(true);

    const result = await getServices().incapacities.register({
      kind: kind.id,
      range,
      imssFolio: folio.trim(),
      umf: umf.trim(),
      coverage:
        chosen === undefined ? '' : `${chosen.displayName} cubre tus pendientes`,
      notes: notes.trim().length > 0 ? notes.trim() : 'Registro enviado por el colaborador.',
      certificatePath,
      replacementPersonId: replacement,
    });

    setBusy(false);

    if (!result.ok) {
      toast.bad(`No pudimos registrar la incapacidad. ${describe(result.error)}`);
      return;
    }

    toast.ok('Incapacidad registrada. RR.HH. validará el certificado y avisará a nómina.');
    await onSubmitted();
    onClose();
  };

  return (
    <WizardShell
      eyebrow="Flujo de incapacidad"
      title="Registrar incapacidad"
      steps={STEPS}
      current={step}
      nextLabel={step === 2 ? 'Enviar a RR.HH. →' : 'Continuar →'}
      nextDisabled={kinds.length === 0}
      busy={busy}
      onBack={() => {
        setStep((s) => Math.max(0, s - 1));
      }}
      onNext={advance}
      onClose={onClose}
    >
      {step === 0 && (
        <>
          <h2 className={w.stepTitle}>Tipo de incapacidad</h2>
          <p className={w.stepNote}>El tipo define el subsidio que cubre el IMSS.</p>

          <div className={styles.kindList}>
            {kinds.map((option) => {
              const picked = option.id === kind?.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  className={cx(
                    styles.kindCard,
                    styles.kindCardAmber,
                    picked && styles.kindCardPickedAmber,
                  )}
                  onClick={() => {
                    setKindId(option.id);
                  }}
                  aria-pressed={picked}
                >
                  <span className={styles.kindText}>
                    <span className={styles.kindLabel}>{option.id}</span>
                    <span className={styles.kindNote}>{option.note}</span>
                  </span>
                  {picked && <span className={styles.pickMarkAmber}>Elegido</span>}
                </button>
              );
            })}
          </div>

          <h3 className={styles.sectionTitle}>Inicio del reposo · Fin del reposo</h3>
          <RangePicker
            value={range}
            onChange={setRange}
            // A leave is filed after the fact as often as before it, so past
            // dates have to be selectable — unlike a vacation request. The grid
            // still opens on the current month rather than the earliest one.
            /*
             * Hasta dónde se puede fechar hacia atrás, según quién captura.
             *
             * Eran seis meses para todo el mundo. Un empleado no debería poder
             * fechar una incapacidad en el pasado —eso es lo que convierte un
             * olvido en días retroactivos que nadie autorizó—, y en cambio quien
             * la valida sí lo necesita: los certificados del IMSS llegan tarde, y
             * es normal registrar en agosto uno expedido en junio.
             *
             * Un año para quien aprueba o valida, y de hoy en adelante para el
             * resto. El servidor no lo revalida todavía: esto acota la pantalla,
             * no es un cerco. Ver docs/PENDIENTE-SERVIDOR.md.
             */
            minimum={canBackdate ? today.addMonths(-12) : today}
            initialMonth={today}
            holidays={holidays.data ?? new Set<string>()}
            busy={new Map()}
          />

          <div className={cx(styles.countPanel, styles.countPanelAmber)}>
            <div className={styles.countLabel}>Días naturales</div>
            <div className={styles.countValue}>{naturalDays}</div>
          </div>
        </>
      )}

      {step === 1 && (
        <>
          <h2 className={w.stepTitle}>{imssOn ? 'Certificado IMSS' : 'Certificado médico'}</h2>
          <p className={w.stepNote}>Obligatorio para que RR.HH. valide el subsidio.</p>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>
              {!imssCertified
                ? 'Folio o referencia del documento (opcional)'
                : imssOn
                  ? 'Folio del certificado IMSS'
                  : 'Folio del certificado médico (opcional)'}
            </span>
            <input
              type="text"
              className={cx(styles.input, folioUnusable && styles.inputInvalid)}
              aria-invalid={folioUnusable}
              maxLength={IMSS_FOLIO_MAX_LENGTH}
              placeholder="Ej. AB123456"
              value={folio}
              onChange={(e) => {
                setFolio(e.target.value);
              }}
            />
            {/*
              Dos mensajes distintos, y la diferencia es la que importa.
              
              El primero es un error: eso no puede ser un folio de nada, y el
              servidor lo rechaza. El segundo es sólo un aviso — el CITT tiene esa
              forma según la documentación pública, pero un certificado privado no la
              tiene y el papel que alguien tiene delante manda sobre nuestra regla.
              El portal **no consulta al IMSS**; ver `ImssFolio.ts`.
            */}
            {folioUnusable ? (
              <span className={styles.fieldError} role="alert">
                Sólo letras, números y guiones.
              </span>
            ) : folioOddShape ? (
              <span className={styles.hourNote}>
                Un folio del IMSS suele ser dos letras y seis dígitos, como AB123456.
                Revisa que esté bien copiado — si tu certificado es privado, déjalo así.
              </span>
            ) : null}
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Unidad médica</span>
            <input
              type="text"
              className={styles.input}
              // The list already prefixes «UMF», so the field asks for the
              // number alone — otherwise the card reads "UMF UMF 21".
              placeholder="Ej. 21"
              value={umf}
              onChange={(e) => {
                setUmf(e.target.value);
              }}
            />
          </label>

          <h3 className={styles.sectionTitle}>Certificado sellado (PDF)</h3>
          <button
            type="button"
            className={cx(styles.drop, certificate !== null && styles.dropFilled)}
            onClick={() => {
              fileRef.current?.click();
            }}
          >
            <span className={cx(styles.pdfIcon, certificate !== null && styles.pdfIconFilled)}>
              PDF
            </span>
            <span className={styles.dropText}>
              <span className={styles.dropName}>
                {uploading ? 'Subiendo…' : (certificate ?? 'Ningún archivo seleccionado')}
              </span>
              <span className={styles.dropHint}>
                Haz clic o arrastra el certificado — máx. 5 MB
              </span>
            </span>
            <Chip
              background={
                certificate === null ? 'var(--color-surface-subtle-alt)' : 'var(--color-success-bg)'
              }
              foreground={
                certificate === null
                  ? 'var(--color-text-tertiary-alt)'
                  : 'var(--color-success-ink)'
              }
            >
              {certificate === null ? 'Requerido' : 'Adjunto'}
            </Chip>
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf"
            className={styles.fileInput}
            onChange={(e) => {
              void attachCertificate(e.target.files?.[0]);
            }}
          />
        </>
      )}

      {step === 2 && (
        <>
          <h2 className={w.stepTitle}>Quién cubre tus pendientes</h2>
          <p className={w.stepNote}>Se avisará a esa persona y a tu líder de sub área.</p>

          <div className={styles.candidateGrid}>
            {(candidates.data ?? []).map((person) => {
              const picked = person.id === replacement;
              return (
                <button
                  key={person.id}
                  type="button"
                  className={cx(styles.candidate, picked && styles.candidatePickedAmber)}
                  onClick={() => {
                    setReplacement(person.id);
                  }}
                  aria-pressed={picked}
                >
                  <Avatar
                    name={person.displayName}
                    color={seedColorForSubArea(person.subAreaId ?? person.displayName)}
                    size={36}
                  />
                  <span className={styles.candidateText}>
                    <span className={styles.candidateName}>{person.displayName}</span>
                    <span className={styles.candidateNote}>{subAreaName}</span>
                  </span>
                  {picked && <span className={styles.pickMarkAmber}>Elegido</span>}
                </button>
              );
            })}
          </div>

          {(candidates.data ?? []).length === 0 && (
            <p className={styles.footNote}>
              No hay nadie más registrado en tu sub área. Avisa a tu líder para que designe la
              cobertura manualmente.
            </p>
          )}

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Notas para RR.HH. (opcional)</span>
            <textarea
              className={styles.textarea}
              rows={3}
              value={notes}
              placeholder="Cualquier detalle que ayude a validar el certificado."
              onChange={(e) => {
                setNotes(e.target.value);
              }}
            />
          </label>

          <dl className={styles.summary}>
            {summary.map((row) => (
              <div key={row.k} className={styles.summaryRow}>
                <dt className={styles.summaryKey}>{row.k}</dt>
                <dd className={styles.summaryValue}>{row.v}</dd>
              </div>
            ))}
          </dl>
        </>
      )}

      {kinds.length === 0 && (
        <p className={styles.footNote}>
          No hay tipos de incapacidad activos. Pide a RR.HH. que habilite al menos uno en Catálogos.
        </p>
      )}
    </WizardShell>
  );
};

const describe = (error: { kind: string }): string => {
  switch (error.kind) {
    case 'permission-denied':
      return 'Tu perfil no puede registrar incapacidades.';
    case 'unavailable':
      return 'No pudimos conectar. Revisa tu red e intenta de nuevo.';
    default:
      return 'Intenta de nuevo en un momento.';
  }
};
